/**
 * Client-side entity wrapper — adds Zustand CRUD mutations, echo-safe
 * broadcast handling, auto-generated slices, and hydration to a shared SyncEntity.
 *
 *
 *   // Auto-generated slice includes: setFolders, addFolder, updateFolder, removeFolder
 *   // In message handler: await folders.applyServerBroadcast(msg, ctx)
 */

import type {
  LWWItem,
  SyncEntity,
} from '@flashka/shared/sync-engine/framework/define-sync-entity';
import { sameEntryContent } from '@flashka/shared/sync-engine/framework/define-sync-entity';
import { mergeItemsLWW } from '@flashka/shared/sync-engine/framework/sync-merge';

import { withServerSource } from '../../internal/framework/mutation-source';
import type { SyncCtx } from '../../internal/hook/sync-context-types';
import { perfLog, perfNow } from '../../internal/perf-log'; // [perf-probe]
import type { FlashkaState } from '../../internal/store/flashka-store';
import { useFlashkaStore } from '../../internal/store/flashka-store';
import type { SyncStorage } from '../../provider/sync-storage';
import { getSyncStorage } from '../../provider/sync-storage';
import { reportSyncStorageError } from '../store/store-utils';
import {
  BULK_COALESCE_MAX_ENTRIES,
  drainPendingBulk,
  schedulePendingBulk,
} from './bulk-coalesce';

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

type SetFn = (
  update: Partial<FlashkaState> | ((s: FlashkaState) => Partial<FlashkaState>),
) => void;

/** Loose view of the Zustand store for dynamic-key access (entity stateKey, method names). */
type DynamicState = FlashkaState & Record<string, unknown>;

function mutate(
  set: SetFn,
  update: Partial<FlashkaState> | ((s: FlashkaState) => Partial<FlashkaState>),
  persist: (storage: SyncStorage) => void,
) {
  set(update);
  persist(getSyncStorage());
}

// ---------------------------------------------------------------------------
// Naming helpers
// ---------------------------------------------------------------------------

function capitalize(s: string): string {
  return s.charAt(0).toUpperCase() + s.slice(1);
}

/** "folders" → "Folder", "labels" → "Label", "globalSrSettings" → "GlobalSrSettings" */
function singularize(name: string): string {
  const cap = capitalize(name);
  return cap.endsWith('s') ? cap.slice(0, -1) : cap;
}

// ---------------------------------------------------------------------------
// Type-level helpers for deriving slice shapes from entity Name + Kind + T
// ---------------------------------------------------------------------------

/** "folders" → "Folder", "globalSrSettings" → "GlobalSrSetting" */
type Singularize<S extends string> = S extends `${infer Rest}s`
  ? Capitalize<Rest>
  : Capitalize<S>;

/** Zustand slice shape for a value entity (e.g. settings, userInfo). */
type ValueSlice<Name extends string, T> = {
  [K in Name]: T;
} & {
  [K in `set${Capitalize<Name>}`]: (data: T) => void;
};

/** Zustand slice shape for an array entity (e.g. folders). T is the item type. */
type ArraySlice<Name extends string, T> = {
  [K in Name]: T[];
} & {
  [K in `set${Capitalize<Name>}`]: (data: T[]) => void;
} & {
  [K in `add${Singularize<Name>}`]: (item: T) => void;
} & {
  [K in `update${Singularize<Name>}`]: (id: string, partial: Partial<T>) => void;
} & {
  [K in `remove${Singularize<Name>}`]: (id: string) => void;
};

/** Zustand slice shape for a record entity (e.g. quizProgress). TEntry is the entry type. */
type RecordSlice<Name extends string, TEntry> = {
  [K in Name]: Record<string, TEntry>;
} & {
  [K in `set${Capitalize<Name>}`]: (data: Record<string, TEntry>) => void;
} & {
  [K in `setEntry${Capitalize<Name>}`]: (entry: TEntry) => void;
} & {
  [K in `removeEntry${Capitalize<Name>}`]: (key: string) => void;
};

/** Zustand slice shape for a nested-record entity (e.g. cards keyed by deckId+cardId).
 *  Value type is `Record<parentId, Record<childPart, T>>`. Granular per-entry
 *  helpers operate on the full TEntry — parent/child are derived from the
 *  entry's compound key (`parentId\x01childPart`). */
type NestedRecordSlice<Name extends string, TEntry> = {
  [K in Name]: Record<string, Record<string, TEntry>>;
} & {
  [K in `set${Capitalize<Name>}`]: (data: Record<string, Record<string, TEntry>>) => void;
} & {
  [K in `setEntry${Capitalize<Name>}`]: (entry: TEntry) => void;
} & {
  [K in `removeEntry${Capitalize<Name>}`]: (parentId: string, childKey: string) => void;
};

/** Zustand slice shape for an indexed-collection entity (e.g. deckProperties
 *  keyed by id). Value type is `Record<primaryKey, T>`. */
type IndexedCollectionSlice<Name extends string, T> = {
  [K in Name]: Record<string, T>;
} & {
  [K in `set${Capitalize<Name>}`]: (data: Record<string, T>) => void;
} & {
  [K in `add${Singularize<Name>}`]: (item: T) => void;
} & {
  [K in `update${Singularize<Name>}`]: (id: string, partial: Partial<T>) => void;
} & {
  [K in `remove${Singularize<Name>}`]: (id: string) => void;
};

/**
 * Derive the slice type from the entity's full config. The shape flags
 * (`nested` for records, `indexById` for collections) drive the branch
 * explicitly, so the slice value type matches the engine's runtime
 * hydration shape 1-to-1 without relying on structural inference of the
 * state type (which over-matches when the entity's item type happens to
 * look like `Record<string, X>`).
 */
export type InferSliceFromConfig<
  State,
  Name extends string,
  Kind extends 'array' | 'collection' | 'record' | 'value',
  Nested extends boolean,
  IndexById extends boolean,
> = [Kind] extends ['value']
  ? ValueSlice<Name, State>
  : [Kind] extends ['record']
    ? // Tuple-wrap to avoid distribution AND to short-circuit on
      // `[Nested] extends [true]` first, which is robust under
      // `strictNullChecks: false` (ts-jest): under non-strict mode
      // `boolean extends true` would otherwise be true and every record
      // entity would land in the nested branch, not just `cards`.
      [Nested] extends [true]
      ? State extends Record<string, Record<string, infer NestedEntry>>
        ? NestedRecordSlice<Name, NestedEntry>
        : never
      : State extends Record<string, infer Entry>
        ? RecordSlice<Name, Entry>
        : never
    : [IndexById] extends [true]
      ? State extends Record<string, infer Item>
        ? IndexedCollectionSlice<Name, Item>
        : never
      : State extends (infer Item)[]
        ? ArraySlice<Name, Item>
        : never;

/**
 * Backwards-compatible wrapper around `InferSliceFromState`. Existing
 * consumers (`ClientSyncEntity`) pass the entity's item type + kind;
 * `DefineStateType` reconstructs the state shape with default flags
 * (no `nested`, no `indexById`).
 */
export type InferSlice<
  T,
  Name extends string,
  Kind extends 'array' | 'collection' | 'record' | 'value',
> = Kind extends 'array' | 'collection'
  ? T extends (infer Item)[]
    ? ArraySlice<Name, Item>
    : never
  : Kind extends 'record'
    ? T extends Record<string, infer Entry>
      ? RecordSlice<Name, Entry>
      : never
    : ValueSlice<Name, T>;

// ---------------------------------------------------------------------------
// Client entity type
// ---------------------------------------------------------------------------

export type ClientSyncEntity<
  T = unknown,
  Name extends string = string,
  Kind extends 'array' | 'collection' | 'record' | 'value' =
    | 'array'
    | 'collection'
    | 'record'
    | 'value',
> = SyncEntity<T, Name, Kind> & {
  applyServerBroadcast: (msg: unknown, ctx: SyncCtx) => Promise<void>;
  /** Handle a record delete broadcast (record entities only). */
  applyServerDeleteBroadcast?: (msg: unknown, ctx: SyncCtx) => Promise<void>;
  /**
   * Handle a record BULK update broadcast (record entities only).
   * Envelope: `{type, entries: Array<{key, entry, updatedAtISO}>, updatedAtISO}`.
   * Persists every entry and records its timestamp when the frame arrives;
   * the STORE publish is coalesced across frames into ONE `setState` under
   * the server source (`bulk-coalesce.ts`). Readers that must see the frames
   * synchronously call `drainPendingBulk()` first.
   */
  applyServerBulkBroadcast?: (msg: unknown, ctx: SyncCtx) => Promise<void>;
  /**
   * Handle a record BULK delete broadcast (record entities with delete only).
   * Envelope: `{type, keys: Array<{key, deletedTimeISO}>, updatedAtISO}`.
   */
  applyServerBulkDeleteBroadcast?: (msg: unknown, ctx: SyncCtx) => Promise<void>;
  /** Create a typed Zustand slice. The return type is inferred from Name + Kind + T. */
  createSlice: (set: SetFn) => InferSlice<T, Name, Kind>;
  /** Method names generated for this entity. */
  methodNames: {
    set: string;
    add?: string;
    update?: string;
    remove?: string;
    setEntry?: string;
    removeEntry?: string;
  };
};

// ---------------------------------------------------------------------------
// Factory
// ---------------------------------------------------------------------------

export function createClientEntity<
  T,
  Name extends string,
  Kind extends 'array' | 'collection' | 'record' | 'value',
>(entity: SyncEntity<T, Name, Kind>): ClientSyncEntity<T, Name, Kind> {
  const { stateKey, tsKey, storageKey } = entity;
  const singular = singularize(entity.name);
  const isArray = entity.type === 'array' || entity.type === 'collection';
  const isCollection = entity.type === 'collection';
  const isRecord = entity.type === 'record';
  // Opt-in state-shape flags — drive the runtime CRUD branch to match
  // the type system (see `define-sync-entity.ts` in sync-engine/framework).
  const nestedRecord = isRecord && Boolean(entity.nested);
  const indexedCollection = isCollection && Boolean(entity.indexById);

  const methodNames = {
    set: `set${capitalize(entity.name)}`,
    ...(isArray
      ? {
          add: `add${singular}`,
          update: `update${singular}`,
          remove: `remove${singular}`,
        }
      : {}),
    ...(isRecord
      ? {
          setEntry: `setEntry${capitalize(entity.name)}`,
          removeEntry: `removeEntry${capitalize(entity.name)}`,
        }
      : {}),
  };

  // ── CRUD methods ─────────────────────────────────────────────────────

  const setData = (zustandSet: SetFn, data: T) => {
    mutate(zustandSet, { [stateKey]: data } as Partial<FlashkaState>, (st) =>
      entity.persistReplace(st, data),
    );
  };

  const addItem = (zustandSet: SetFn, item: unknown) => {
    // Collection entities: stamp the item with updatedAtISO
    const stamped: LWWItem = isCollection
      ? { ...(item as object), updatedAtISO: new Date().toISOString() }
      : (item as LWWItem);
    const pk = entity.primaryKey;
    if (indexedCollection && pk) {
      // Indexed shape: state is `Record<id, T>`. Upsert by id.
      const id = stamped[pk] as string;
      mutate(
        zustandSet,
        (s) =>
          ({
            [stateKey]: {
              ...((s as DynamicState)[stateKey] as Record<string, LWWItem>),
              [id]: stamped,
            },
          }) as Partial<FlashkaState>,
        (st) => st.put(storageKey, stamped),
      );
      return;
    }
    mutate(
      zustandSet,
      // Upsert by primary key. Dexie .put() and the server's collection
      // detectDirty both dedupe by PK, so Zustand must too — otherwise a
      // duplicate add yields two entries in memory but one after refresh.
      // In dev, warn when overwriting to surface unintentional duplicates.
      (s) => {
        const arr = (s as DynamicState)[stateKey] as LWWItem[];
        if (pk) {
          const id = stamped[pk];
          const existingIdx = arr.findIndex((x) => x[pk] === id);
          if (existingIdx >= 0) {
            if (process.env.NODE_ENV !== 'production') {
              console.warn(
                `[sync] ${entity.name}.add: overwriting existing item ` +
                  `${pk}=${String(id)}. Use update() for partial edits.`,
              );
            }
            const next = arr.slice();
            next[existingIdx] = stamped;
            return { [stateKey]: next } as Partial<FlashkaState>;
          }
        }
        return { [stateKey]: [...arr, stamped] } as Partial<FlashkaState>;
      },
      (st) => st.put(storageKey, stamped),
    );
  };

  const updateItem = (zustandSet: SetFn, id: string, partial: unknown) => {
    // Collection entities: stamp the updated item with updatedAtISO
    const patchObj: LWWItem = isCollection
      ? { ...(partial as object), updatedAtISO: new Date().toISOString() }
      : (partial as LWWItem);
    const pk = entity.primaryKey;
    if (indexedCollection) {
      mutate(
        zustandSet,
        (s) => {
          const bucket = (s as DynamicState)[stateKey] as Record<string, LWWItem>;
          const prev = bucket[id];
          if (!prev) return { [stateKey]: bucket } as Partial<FlashkaState>;
          return {
            [stateKey]: { ...bucket, [id]: { ...prev, ...patchObj } },
          } as Partial<FlashkaState>;
        },
        (st) =>
          st.updateByKey(storageKey, id, patchObj as Partial<Record<string, unknown>>),
      );
      return;
    }
    mutate(
      zustandSet,
      (s) =>
        ({
          [stateKey]: ((s as DynamicState)[stateKey] as LWWItem[]).map((x) =>
            pk && x[pk] === id ? { ...x, ...patchObj } : x,
          ),
        }) as Partial<FlashkaState>,
      (st) =>
        st.updateByKey(storageKey, id, patchObj as Partial<Record<string, unknown>>),
    );
  };

  const removeItem = (zustandSet: SetFn, id: string) => {
    const pk = entity.primaryKey;
    if (indexedCollection) {
      // Soft delete: stamp deletedTimeISO + updatedAtISO on the row.
      const now = new Date().toISOString();
      const patch = { deletedTimeISO: now, updatedAtISO: now };
      mutate(
        zustandSet,
        (s) => {
          const bucket = (s as DynamicState)[stateKey] as Record<string, LWWItem>;
          const prev = bucket[id];
          if (!prev) return { [stateKey]: bucket } as Partial<FlashkaState>;
          return {
            [stateKey]: { ...bucket, [id]: { ...prev, ...patch } },
          } as Partial<FlashkaState>;
        },
        (st) => st.updateByKey(storageKey, id, patch),
      );
      return;
    }
    if (isCollection) {
      // Soft delete: mark with deletedTimeISO + stamp updatedAtISO
      const now = new Date().toISOString();
      const patch = { deletedTimeISO: now, updatedAtISO: now };
      mutate(
        zustandSet,
        (s) =>
          ({
            [stateKey]: ((s as DynamicState)[stateKey] as LWWItem[]).map((x) =>
              pk && x[pk] === id ? { ...x, ...patch } : x,
            ),
          }) as Partial<FlashkaState>,
        (st) => st.updateByKey(storageKey, id, patch),
      );
      return;
    }
    // Hard delete for plain array entities
    mutate(
      zustandSet,
      (s) =>
        ({
          [stateKey]: ((s as DynamicState)[stateKey] as LWWItem[]).filter(
            (x) => !(pk && x[pk] === id),
          ),
        }) as Partial<FlashkaState>,
      (st) => st.removeByKey(storageKey, id),
    );
  };

  // ── Record CRUD methods ──────────────────────────────────────────────

  const setEntry = (zustandSet: SetFn, entry: unknown) => {
    if (!entity.entryKey) return;
    const stamped = {
      ...(entry as object),
      updatedAtISO: new Date().toISOString(),
    } as LWWItem;
    const key = entity.entryKey(stamped);
    // A keyless entry (e.g. a deckProperties row missing `id`) would land
    // in the store as `state["undefined"]`, persist under a coerced ''
    // primary key, and be rejected by the server's push validation —
    // permanent local garbage with no sync value. Refuse it instead.
    if (typeof key !== 'string' || key.length === 0) {
      console.error(
        `[sync] setEntry${capitalize(entity.name)} refused a keyless entry`,
        entry,
      );
      return;
    }
    if (nestedRecord) {
      // Nested shape: state is `Record<parentId, Record<childPart, T>>`.
      // Parent id is the first segment of the entry key before `\x01`;
      // child part is the rest. Compound keys are an invariant of
      // nested-state entities — callers without a `\x01` delimiter
      // shouldn't reach this branch.
      const sep = key.indexOf('\x01');
      if (sep === -1) return;
      const parentId = key.slice(0, sep);
      const childPart = key.slice(sep + 1);
      mutate(
        zustandSet,
        (s) => {
          const root = (s as DynamicState)[stateKey] as Record<
            string,
            Record<string, LWWItem>
          >;
          return {
            [stateKey]: {
              ...root,
              [parentId]: { ...(root[parentId] ?? {}), [childPart]: stamped },
            },
          } as Partial<FlashkaState>;
        },
        (st) => st.put(storageKey, stamped),
      );
      return;
    }
    mutate(
      zustandSet,
      (s) =>
        ({
          [stateKey]: {
            ...((s as DynamicState)[stateKey] as Record<string, LWWItem>),
            [key]: stamped,
          },
        }) as Partial<FlashkaState>,
      (st) => st.put(storageKey, stamped),
    );
  };

  const removeEntry = (zustandSet: SetFn, keyOrParent: string, childKey?: string) => {
    if (nestedRecord && childKey !== undefined) {
      // Nested signature: (parentId, childKey)
      const parentId = keyOrParent;
      mutate(
        zustandSet,
        (s) => {
          const root = (s as DynamicState)[stateKey] as Record<
            string,
            Record<string, LWWItem>
          >;
          const bucket = root[parentId];
          if (!bucket) return { [stateKey]: root } as Partial<FlashkaState>;
          const nextBucket = { ...bucket };
          delete nextBucket[childKey];
          const next = { ...root };
          if (Object.keys(nextBucket).length === 0) delete next[parentId];
          else next[parentId] = nextBucket;
          return { [stateKey]: next } as Partial<FlashkaState>;
        },
        (st) =>
          st.removeByKey(
            storageKey,
            entity.decomposeStorageKey
              ? (entity.decomposeStorageKey(`${parentId}\x01${childKey}`) as
                  | string
                  | unknown[])
              : `${parentId}\x01${childKey}`,
          ),
      );
      return;
    }
    const key = keyOrParent;
    mutate(
      zustandSet,
      (s) => {
        const next = {
          ...((s as DynamicState)[stateKey] as Record<string, LWWItem>),
        };
        delete next[key];
        return { [stateKey]: next } as Partial<FlashkaState>;
      },
      (st) =>
        st.removeByKey(
          storageKey,
          entity.decomposeStorageKey
            ? (entity.decomposeStorageKey(key) as string | unknown[])
            : key,
        ),
    );
  };

  // ── Broadcast handling ───────────────────────────────────────────────

  /** Whether a peer's broadcast row is merged into the local one (see
   *  `foldIntoLocal`) rather than replacing it. Opt-in per entity. */
  const foldsBroadcasts = entity.foldBroadcasts === true && Boolean(entity.mergeEntry);

  /** The store's current row for a record key (flat or `parent\x01child`). */
  const readLocalEntry = (key: string): LWWItem | undefined => {
    const root = (useFlashkaStore.getState() as DynamicState)[stateKey];
    if (!root) return undefined;
    if (!nestedRecord) return (root as Record<string, LWWItem>)[key];
    const sep = key.indexOf('\x01');
    if (sep === -1) return undefined;
    return (root as Record<string, Record<string, LWWItem> | undefined>)[
      key.slice(0, sep)
    ]?.[key.slice(sep + 1)];
  };

  /**
   * Fold a peer's row (a broadcast) into the row this device holds — ONLY
   * for an entity that declares `foldBroadcasts` (today: `deckProperties`,
   * for its per-entry-clocked notes/maps index, and `cards`, for its review
   * history). Every other entity, with
   * or without a `mergeEntry`, takes the broadcast row whole and stamps
   * its clock, exactly as before the index existed: `mergeQuizEntry` is
   * not a join (an attempt both sides hold resolves to the incoming copy),
   * so folding a peer's quiz rename into a stale local row would overwrite
   * it, leave the row dirty and push the old name back — see
   * `RecordEntityInput.foldBroadcasts`.
   *
   * The server excludes the sender from every broadcast and merges pushes
   * per entry, so a broadcast is a PEER's write folded into the server's
   * row — it does not carry this device's own in-flight push. Replacing
   * the local row with it dropped whatever this device had written and not
   * yet heard back about: the phone's "Note 2", pushed a moment before the
   * web's newer row arrived, vanished from the phone's own grid, and since
   * the phone's synced clock then matched the server's, no later diff
   * brought it back. Merging keeps both halves; the union is what the
   * server lands on once the in-flight push arrives.
   *
   * `keptLocal` says the merge kept something the broadcast lacked. The
   * caller then leaves the synced ts where it was so the next flush
   * re-pushes the row (idempotent on the server, which folds it in and
   * writes nothing when it already has it) — the same discipline as
   * `applySyncDiff`. Recording the broadcast's ts would mark the local half
   * as synced and strand it on this device.
   */
  const foldIntoLocal = (
    key: string,
    entry: LWWItem,
  ): { entry: LWWItem; keptLocal: boolean } => {
    const merge = foldsBroadcasts ? entity.mergeEntry : undefined;
    if (!merge) return { entry, keptLocal: false };
    const local = readLocalEntry(key);
    if (!local) return { entry, keptLocal: false };
    const merged = merge(entry, local);
    return { entry: merged, keptLocal: !sameEntryContent(merged, entry) };
  };

  const applyServerBroadcast = async (msg: unknown, ctx: SyncCtx) => {
    const m = msg as { key?: string; entry?: LWWItem; updatedAtISO?: string };

    if (isRecord && entity.entryKey) {
      // Record update broadcast: { key, entry, updatedAtISO }
      // Frames staged for this entity land first so this entry is applied on
      // top of them, not overwritten by their later publish.
      drainPendingBulk(stateKey);
      const key = m.key as string;
      let entry = m.entry as LWWItem;

      // Same ingest guard as the sync-diff path (applySyncDiff): a
      // malformed entry must never reach the store or persistence, and a
      // dropped entry records NO server ts — so a later repaired row still
      // delivers. No current server emitter can broadcast a malformed
      // deckProperties entry (push broadcasts re-emit Zod-validated
      // entries); this is boundary parity, not a live hole.
      if (entity.normalizeServerEntry) {
        const normalized = entity.normalizeServerEntry(key, entry);
        if (normalized === null) return;
        entry = normalized as LWWItem;
      }

      const folded = foldIntoLocal(key, entry);
      entry = folded.entry;

      // Resolved upfront so both the in-memory set and the persist write
      // can reuse it. For nested records the wire format encodes the
      // parent in the key (e.g. `deckId\x01cardId`), not always in the
      // entry body — the persist write below has to stamp it back on
      // so the Dexie row stays queryable by parent.
      let persistEntry: LWWItem = entry;
      withServerSource(() => {
        if (nestedRecord) {
          // Write to `state[stateKey][parentId][childPart]`. Compound key
          // is an invariant for nested-state entities.
          const sep = key.indexOf('\x01');
          if (sep === -1) return;
          const parentId = key.slice(0, sep);
          const childPart = key.slice(sep + 1);
          if (entity.parentKeyField) {
            persistEntry = {
              ...(entry as Record<string, unknown>),
              [entity.parentKeyField]: parentId,
            } as LWWItem;
          }
          const current = (useFlashkaStore.getState() as DynamicState)[
            stateKey
          ] as Record<string, Record<string, LWWItem>>;
          useFlashkaStore.setState({
            [stateKey]: {
              ...current,
              [parentId]: { ...(current[parentId] ?? {}), [childPart]: entry },
            },
          } as Partial<FlashkaState>);
        } else {
          const current = (useFlashkaStore.getState() as DynamicState)[
            stateKey
          ] as Record<string, LWWItem>;
          useFlashkaStore.setState({
            [stateKey]: { ...current, [key]: entry },
          } as Partial<FlashkaState>);
        }
      });
      // Persist only the changed entry (not the full record)
      getSyncStorage().put(storageKey, persistEntry);

      // The merge kept a local half the server lacks: leave the synced ts
      // alone so the next flush pushes the row (see `foldIntoLocal`).
      if (folded.keptLocal) return;
      const updatedAtISO = entry?.updatedAtISO ?? m.updatedAtISO;
      if (updatedAtISO && entity.tsKeyPrefix) {
        // Drain first — a checkpoint is never persisted ahead of the data it
        // certifies. `put` is fire-and-forget; `saveServerSyncedTs` is awaited.
        // Saving the second without the first means a lost write is recorded
        // as synced, and the server then answers every later sync-init with
        // "you already have it". See `handleSyncDiff` for the measured case.
        await getSyncStorage().flush?.();
        ctx.serverTsRef.current[entity.tsKeyPrefix + key] = updatedAtISO;
        await ctx.saveServerSyncedTs(ctx.serverTsRef.current);
      }
      return;
    }

    const data = entity.extractFromMessage(msg);

    if (isCollection && entity.primaryKey) {
      // Per-item merge: incoming broadcast contains only changed items
      const pk = entity.primaryKey;
      withServerSource(() => {
        if (indexedCollection) {
          const currentBucket = (useFlashkaStore.getState() as DynamicState)[
            stateKey
          ] as Record<string, LWWItem>;
          const currentArr = Object.values(currentBucket);
          const merged = mergeItemsLWW(currentArr, data as LWWItem[], pk);
          const nextBucket: Record<string, LWWItem> = {};
          for (const item of merged) {
            const id = (item as Record<string, unknown>)[pk] as string | undefined;
            if (id !== undefined) nextBucket[id] = item;
          }
          useFlashkaStore.setState({
            [stateKey]: nextBucket,
          } as Partial<FlashkaState>);
        } else {
          const current = (useFlashkaStore.getState() as DynamicState)[
            stateKey
          ] as LWWItem[];
          const merged = mergeItemsLWW(current, data as LWWItem[], pk);
          useFlashkaStore.setState({
            [stateKey]: merged,
          } as Partial<FlashkaState>);
        }
      });
      // Persist the full merged state
      const latest = (useFlashkaStore.getState() as DynamicState)[stateKey] as T;
      entity.persistReplace(getSyncStorage(), latest);
    } else {
      withServerSource(() => {
        useFlashkaStore.setState({ [stateKey]: data } as Partial<FlashkaState>);
      });
      entity.persistReplace(getSyncStorage(), data);
    }

    const updatedAtISO = (msg as { updatedAtISO?: string }).updatedAtISO;
    if (updatedAtISO) {
      // Same ordering rule as the record arm above. This one matters more:
      // `persistReplace` on a collection is `clear()` + one put per row, so a
      // checkpoint saved ahead of it can certify an EMPTY table.
      await getSyncStorage().flush?.();
      ctx.serverTsRef.current[tsKey] = updatedAtISO;
      await ctx.saveServerSyncedTs(ctx.serverTsRef.current);
    }
  };

  /** Handle a record delete broadcast: { key } */
  const applyServerDeleteBroadcast = async (msg: unknown, ctx: SyncCtx) => {
    const key = (msg as { key: string }).key;
    // A pending upsert of this key must not publish after the delete and
    // resurrect the row.
    drainPendingBulk(stateKey);

    withServerSource(() => {
      if (nestedRecord) {
        const sep = key.indexOf('\x01');
        const parentId = sep === -1 ? key : key.slice(0, sep);
        const childPart = sep === -1 ? key : key.slice(sep + 1);
        const root = (useFlashkaStore.getState() as DynamicState)[stateKey] as Record<
          string,
          Record<string, LWWItem>
        >;
        const bucket = root[parentId];
        if (!bucket) return;
        const nextBucket = { ...bucket };
        delete nextBucket[childPart];
        const next = { ...root };
        if (Object.keys(nextBucket).length === 0) delete next[parentId];
        else next[parentId] = nextBucket;
        useFlashkaStore.setState({
          [stateKey]: next,
        } as Partial<FlashkaState>);
        return;
      }
      const current = {
        ...((useFlashkaStore.getState() as DynamicState)[stateKey] as Record<
          string,
          LWWItem
        >),
      };
      delete current[key];
      useFlashkaStore.setState({
        [stateKey]: current,
      } as Partial<FlashkaState>);
    });
    // Delete only the removed entry (not rewrite the full record)
    getSyncStorage().removeByKey(
      storageKey,
      entity.decomposeStorageKey
        ? (entity.decomposeStorageKey(key) as string | unknown[])
        : key,
    );

    if (entity.tsKeyPrefix) {
      delete ctx.serverTsRef.current[entity.tsKeyPrefix + key];
      await ctx.saveServerSyncedTs(ctx.serverTsRef.current);
    }
  };

  // ── Coalesced bulk publish (see `bulk-coalesce.ts`) ──────────────────
  // The draft holds the ENTRIES of every frame since the last publish, keyed
  // the way the store is, plus the identity of the store's map/bucket when the
  // first frame for it was staged (`base`). At publish the outer map and each
  // touched bucket are cloned from the CURRENT store once — a 7k-row stream
  // clones its bucket a handful of times instead of 70 — and a key the store
  // changed since staging (`current[key] !== base[key]`: a local edit landed
  // in the window) keeps the store's value. That is exactly the order the
  // per-frame apply had: the frame landed first, the later local write won.
  type Draft = {
    rows: Record<string, LWWItem>;
    base: Record<string, LWWItem> | undefined;
  };
  let pendingFlat: Draft | null = null;
  let pendingNested: Map<string, Draft> | null = null;
  let pendingCount = 0;

  /** `{...current, ...rows}`, except that a key the store changed since the
   *  draft was opened is resolved LAST-WRITER-WINS by timestamp — or, for an
   *  entity that declares `foldBroadcasts`, by its `mergeEntry`, so a local
   *  edit made in the window and the staged peer row both land (and the
   *  merged row is re-persisted: the edit's own put ran before the publish
   *  and predates the union). `parentId` is the nested bucket's id, for the
   *  persist stamp. */
  const overlayDraft = (
    current: Record<string, LWWItem> | undefined,
    draft: Draft,
    parentId?: string,
  ): Record<string, LWWItem> => {
    const next = { ...(current ?? {}) };
    if (current === draft.base) {
      Object.assign(next, draft.rows);
      return next;
    }
    for (const key of Object.keys(draft.rows)) {
      const staged = draft.rows[key]!;
      const inStore = current?.[key];
      if (inStore === draft.base?.[key]) {
        next[key] = staged;
        continue;
      }
      if (foldsBroadcasts && entity.mergeEntry && inStore) {
        const merged = entity.mergeEntry(staged, inStore);
        next[key] = merged;
        const row =
          parentId !== undefined && entity.parentKeyField
            ? ({
                ...(merged as Record<string, unknown>),
                [entity.parentKeyField]: parentId,
              } as LWWItem)
            : merged;
        getSyncStorage().put(storageKey, row);
        continue;
      }
      // Changed since staging: a local write landed in the window, so it
      // normally wins — that is the order the per-frame apply had. But
      // reference identity alone said so even when the staged row was a PEER
      // frame that arrived AFTER the local edit and carried a NEWER timestamp:
      // disk and `serverTsRef` (both per frame) took it, memory kept the older
      // local value, and the three disagreed for the rest of the session with
      // nothing left to re-push. Compare timestamps instead.
      const stagedTs = staged.updatedAtISO;
      const storeTs = inStore?.updatedAtISO;
      if (stagedTs && (!storeTs || stagedTs > storeTs)) next[key] = staged;
    }
    return next;
  };

  const takeDraft = () => {
    const flat = pendingFlat;
    const nested = pendingNested;
    const count = pendingCount;
    pendingFlat = null;
    pendingNested = null;
    pendingCount = 0;
    return { flat, nested, count };
  };

  const publishPendingBulk = () => {
    const { flat, nested, count } = takeDraft();
    if (!flat && !nested) return;
    const perfT0 = perfNow(); // [perf-probe]
    // MUST run under the server source: a deferred write outside it reads as
    // a local edit and the dirty scan re-pushes the whole stream.
    withServerSource(() => {
      if (nested) {
        const current = (useFlashkaStore.getState() as DynamicState)[stateKey] as Record<
          string,
          Record<string, LWWItem>
        >;
        const next = { ...current };
        for (const [parentId, draft] of nested) {
          next[parentId] = overlayDraft(current[parentId], draft, parentId);
        }
        useFlashkaStore.setState({ [stateKey]: next } as Partial<FlashkaState>);
      } else if (flat) {
        const current = (useFlashkaStore.getState() as DynamicState)[stateKey] as Record<
          string,
          LWWItem
        >;
        useFlashkaStore.setState({
          [stateKey]: overlayDraft(current, flat),
        } as Partial<FlashkaState>);
      }
    });
    // [perf-probe] setState above synchronously runs every store subscriber
    // (the whole deck page re-render included), so this span IS the per-batch
    // React cost, not just the map merge.
    perfLog(`sync bulk ${stateKey} store-write+subscribers`, perfT0, () => ({
      entries: count,
    }));
  };

  const stagePendingBulk = (entries: Array<{ key: string; entry: LWWItem }>) => {
    const wasEmpty = pendingCount === 0;
    if (nestedRecord) {
      const drafts = pendingNested ?? (pendingNested = new Map());
      let current: Record<string, Record<string, LWWItem>> | null = null;
      for (const { key, entry } of entries) {
        const sep = key.indexOf('\x01');
        if (sep === -1) continue;
        const parentId = key.slice(0, sep);
        let draft = drafts.get(parentId);
        if (!draft) {
          current ??= (useFlashkaStore.getState() as DynamicState)[stateKey] as Record<
            string,
            Record<string, LWWItem>
          >;
          draft = { rows: {}, base: current[parentId] };
          drafts.set(parentId, draft);
        }
        draft.rows[key.slice(sep + 1)] = entry;
        pendingCount++;
      }
    } else {
      const draft =
        pendingFlat ??
        (pendingFlat = {
          rows: {},
          base: (useFlashkaStore.getState() as DynamicState)[stateKey] as Record<
            string,
            LWWItem
          >,
        });
      for (const { key, entry } of entries) {
        draft.rows[key] = entry;
        pendingCount++;
      }
    }
    if (pendingCount === 0) return;
    if (wasEmpty) {
      schedulePendingBulk(stateKey, {
        publish: publishPendingBulk,
        discard: () => {
          takeDraft();
        },
      });
    }
    // Hard cap: a long stream publishes every ~20 frames rather than once at
    // the end, bounding both the draft and the batch's subscriber cost.
    if (pendingCount >= BULK_COALESCE_MAX_ENTRIES) drainPendingBulk(stateKey);
  };

  /**
   * Bulk update broadcast handler (record entities only).
   *
   * Counterpart to the server's `createBulkBroadcastMessage` factory in
   * `sync-engine/framework/define-sync-entity.ts:1077`. The server has
   * emitted `${name}-bulk-updated` for every record-entity update since
   * commit `205273b` (batch dirty entries into bulk wire frames), so a
   * client without this handler silently drops every multi-tab fan-out
   * — the optimistic write stays on the originating tab, peers never
   * re-render, and `serverTsRef` falls out of date.
   *
   * Persistence and timestamps run per frame. The STORE publish does not:
   * entries are staged into this entity's draft and published by
   * `bulk-coalesce.ts` in ONE `withServerSource` + ONE `setState` per
   * animation frame (or at `BULK_COALESCE_MAX_ENTRIES`), so a streamed deck
   * costs one subscriber run per batch of frames instead of one per frame,
   * and clones each touched bucket once per batch instead of once per frame.
   */
  const applyServerBulkBroadcast = async (msg: unknown, ctx: SyncCtx) => {
    const m = msg as {
      entries?: Array<{ key: string; entry: LWWItem; updatedAtISO?: string }>;
    };
    let entries = m.entries ?? [];
    // Same ingest guard as `applyServerBroadcast` above — drop malformed
    // entries (no store write, no persist, no ts recorded).
    if (entity.normalizeServerEntry) {
      const guard = entity.normalizeServerEntry;
      entries = entries.flatMap((e) => {
        const normalized = guard(e.key, e.entry);
        return normalized === null ? [] : [{ ...e, entry: normalized as LWWItem }];
      });
    }
    if (entries.length === 0) return;

    // Fold each row into the one this device holds (see `foldIntoLocal`) —
    // a reconnecting peer's flush arrives as a bulk frame carrying the
    // server's merged rows, and must not drop this device's in-flight half.
    // Keys where the merge kept something local record no ts below.
    const keptLocal = new Set<string>();
    if (foldsBroadcasts) {
      entries = entries.map((e) => {
        const folded = foldIntoLocal(e.key, e.entry);
        if (folded.keptLocal) keptLocal.add(e.key);
        return folded.entry === e.entry ? e : { ...e, entry: folded.entry };
      });
    }

    stagePendingBulk(entries);

    const perfT1 = perfNow(); // [perf-probe]
    const storage = getSyncStorage();
    // Mirror of the parent-stamp logic in `applyServerBroadcast`: nested
    // wire entries encode the parent in the key, not always the body, so
    // re-stamp it before persist or the Dexie row loses its parent.
    const parentField = nestedRecord ? entity.parentKeyField : undefined;
    const rows: LWWItem[] = [];
    for (const { key, entry } of entries) {
      let row: LWWItem = entry;
      if (parentField) {
        const sep = key.indexOf('\x01');
        if (sep !== -1) {
          const parentId = key.slice(0, sep);
          row = {
            ...(entry as Record<string, unknown>),
            [parentField]: parentId,
          } as LWWItem;
        }
      }
      rows.push(row);
    }
    // One batched write per frame where the adapter has one (web: one
    // IndexedDB transaction instead of one per row). Issued now, in frame
    // order, and tracked by the adapter's `flush()` — not awaited, exactly
    // like the per-row puts it replaces.
    if (storage.bulkPut) {
      storage.bulkPut(storageKey, rows).catch((err) => {
        reportSyncStorageError(err, `bulkPut(${storageKey})-frame`);
      });
    } else {
      for (const row of rows) storage.put(storageKey, row);
    }
    // [perf-probe] fire-and-forget writes — this span is call/serialize
    // overhead on the main thread, not IDB commit time.
    perfLog(`sync bulk ${stateKey} storage.put loop`, perfT1, () => ({
      rows: entries.length,
    }));

    if (entity.tsKeyPrefix) {
      let touched = false;
      for (const { key, entry, updatedAtISO } of entries) {
        if (keptLocal.has(key)) continue;
        const ts = (entry?.updatedAtISO as string | undefined) ?? updatedAtISO;
        if (ts) {
          ctx.serverTsRef.current[entity.tsKeyPrefix + key] = ts;
          touched = true;
        }
      }
      if (touched) {
        const perfT2 = perfNow(); // [perf-probe]
        await ctx.saveServerSyncedTs(ctx.serverTsRef.current);
        perfLog(`sync bulk ${stateKey} save-ts`, perfT2, () => ({
          tsEntries: Object.keys(ctx.serverTsRef.current).length,
        }));
      }
    }
  };

  /**
   * Bulk delete broadcast handler (record entities with delete only).
   * Mirrors `applyServerBulkBroadcast` but operates on the `keys` array
   * of `{key, deletedTimeISO}` envelopes.
   */
  const applyServerBulkDeleteBroadcast = async (msg: unknown, ctx: SyncCtx) => {
    const m = msg as { keys?: Array<{ key: string; deletedTimeISO?: string }> };
    const keys = m.keys ?? [];
    if (keys.length === 0) return;
    // As in `applyServerDeleteBroadcast`: no resurrection by a later publish.
    drainPendingBulk(stateKey);

    withServerSource(() => {
      if (nestedRecord) {
        const current = (useFlashkaStore.getState() as DynamicState)[stateKey] as Record<
          string,
          Record<string, LWWItem>
        >;
        const next = { ...current };
        const touchedParents = new Set<string>();
        for (const { key } of keys) {
          const sep = key.indexOf('\x01');
          if (sep === -1) continue;
          const parentId = key.slice(0, sep);
          const childPart = key.slice(sep + 1);
          if (!next[parentId]) continue;
          if (!touchedParents.has(parentId)) {
            next[parentId] = { ...next[parentId] };
            touchedParents.add(parentId);
          }
          delete next[parentId][childPart];
          if (Object.keys(next[parentId]).length === 0) delete next[parentId];
        }
        useFlashkaStore.setState({ [stateKey]: next } as Partial<FlashkaState>);
      } else {
        const current = {
          ...((useFlashkaStore.getState() as DynamicState)[stateKey] as Record<
            string,
            LWWItem
          >),
        };
        for (const { key } of keys) delete current[key];
        useFlashkaStore.setState({ [stateKey]: current } as Partial<FlashkaState>);
      }
    });

    const storage = getSyncStorage();
    const storageKeys = keys.map(({ key }) =>
      entity.decomposeStorageKey
        ? (entity.decomposeStorageKey(key) as string | unknown[])
        : key,
    );
    // One batched delete per frame where the adapter has one.
    if (storage.bulkRemove) storage.bulkRemove(storageKey, storageKeys);
    else for (const k of storageKeys) storage.removeByKey(storageKey, k);

    if (entity.tsKeyPrefix) {
      for (const { key } of keys) {
        delete ctx.serverTsRef.current[entity.tsKeyPrefix + key];
      }
      await ctx.saveServerSyncedTs(ctx.serverTsRef.current);
    }
  };

  // ── Auto-generated slice ─────────────────────────────────────────────

  const createSlice = (set: SetFn): InferSlice<T, Name, Kind> => {
    const slice: Record<string, unknown> = {
      [stateKey]: entity.initial,
      [methodNames.set]: (data: T) => setData(set, data),
    };

    if (isArray && methodNames.add && methodNames.update && methodNames.remove) {
      slice[methodNames.add] = (item: unknown) => addItem(set, item);
      slice[methodNames.update] = (id: string, partial: unknown) =>
        updateItem(set, id, partial);
      slice[methodNames.remove] = (id: string) => removeItem(set, id);
    }

    if (isRecord && methodNames.setEntry && methodNames.removeEntry) {
      slice[methodNames.setEntry] = (entry: unknown) => setEntry(set, entry);
      // Nested-record signature: (parentId, childKey). Flat: (key).
      slice[methodNames.removeEntry] = nestedRecord
        ? (parentId: string, childKey: string) => removeEntry(set, parentId, childKey)
        : (key: string) => removeEntry(set, key);
    }

    return slice as InferSlice<T, Name, Kind>;
  };

  return {
    ...entity,
    applyServerBroadcast,
    applyServerDeleteBroadcast: isRecord ? applyServerDeleteBroadcast : undefined,
    applyServerBulkBroadcast: isRecord ? applyServerBulkBroadcast : undefined,
    applyServerBulkDeleteBroadcast: isRecord ? applyServerBulkDeleteBroadcast : undefined,
    createSlice,
    methodNames,
  };
}
